import { Component, OnInit, HostListener, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ProjectService } from '../../services/project.service';
import { ConsultationService } from '../../services/consultation.service';
import { Project } from '../../models/project.model';
import { onImageError } from '../../utils/media-url.util';

@Component({
  selector: 'app-project-detail',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './project-detail.component.html',
  styleUrl: './project-detail.component.scss'
})
export class ProjectDetailComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private projectService = inject(ProjectService);
  private consultationService = inject(ConsultationService);

  project = signal<Project | null>(null);
  selectedGalleryImage = signal<string>('');
  isVideoPlaying = signal<boolean>(false);
  lightboxIndex = signal<number>(-1);
  activePhotoIndex = signal<number>(0);
  galleryViewMode = signal<'mosaic' | 'grid'>('mosaic');
  onImgErr = onImageError;

  ngOnInit() {
    this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      if (id) {
        const found = this.projectService.getProjectById(id);
        if (found) {
          this.project.set(found);
          this.selectedGalleryImage.set(found.image);
          this.activePhotoIndex.set(0);
          window.scrollTo(0, 0);
        } else {
          this.router.navigate(['/']);
        }
      }
    });
  }

  setGalleryViewMode(mode: 'mosaic' | 'grid') {
    this.galleryViewMode.set(mode);
  }

  selectGalleryImage(img: string) {
    this.selectedGalleryImage.set(img);
  }

  openImageLightbox(index: number) {
    this.lightboxIndex.set(index);
  }

  closeImageLightbox() {
    this.lightboxIndex.set(-1);
  }

  nextImage(event?: Event) {
    if (event) event.stopPropagation();
    const p = this.project();
    if (!p || !p.galleryImages || p.galleryImages.length === 0) return;
    const current = this.lightboxIndex();
    const next = (current + 1) % p.galleryImages.length;
    this.lightboxIndex.set(next);
  }

  prevImage(event?: Event) {
    if (event) event.stopPropagation();
    const p = this.project();
    if (!p || !p.galleryImages || p.galleryImages.length === 0) return;
    const current = this.lightboxIndex();
    const prev = (current - 1 + p.galleryImages.length) % p.galleryImages.length;
    this.lightboxIndex.set(prev);
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (this.lightboxIndex() >= 0) {
      if (event.key === 'ArrowRight') {
        this.nextImage();
      } else if (event.key === 'ArrowLeft') {
        this.prevImage();
      } else if (event.key === 'Escape') {
        this.closeImageLightbox();
      }
    }
  }

  toggleVideoPlay() {
    this.isVideoPlaying.update(v => !v);
  }

  requestConsultationForProject() {
    const p = this.project();
    if (p) {
      this.consultationService.openModal({
        projectType: `Inquiry regarding ${p.title} (${p.suburb})`
      });
    }
  }
}
